/* global Theme */
import { Component } from "@theme/component";
import { ThemeEvents, CartAddEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import { cmToInch, cmToInchCeil, formatFeetInches } from "@theme/length-units";
import { calculateTotalMinorUnits } from "./custom-size-pricing.js";
import { buildInquiryFormUrl, customerFromDataset } from "./inquiry-form-url.js";
import { toggleBackingInfoFromEvent } from "./toggle-backing-info.js";
import { formatMoney } from "./money-format.js";

/** Must match the `data-option-name` values rendered in custom-size-backing-underlay.liquid. */
const OPTION_BACKING = "Backing";
const OPTION_UNDERLAY = "Underlay";

/** @extends {Component} */
export default class CustomSizeConfigurator extends Component {
  /** @type {number} cm */
  #width = 0;

  /** @type {number} cm */
  #length = 0;

  /** @type {string} */
  #selectedOptionsText = "";

  /** @type {number} CS m² price in minor units */
  #pricePerM2 = 0;

  /** @type {number} */
  #minorUnitFactor = 100;

  /** @type {"rectangle" | "oval" | "round"} */
  #shape = "rectangle";

  /** @type {string} */
  #moneyFormat = "${{amount}}";

  /** @type {Set<string>} */
  #standardSizes = new Set();

  /** @type {Array<{id:number, pricePerM2:number, backing:boolean, underlay:boolean, sku?: string}>} */
  #variants = [];

  /** @type {string} */
  #sizeLabel = "Custom size";

  /** @type {boolean} */
  #isAdding = false;

  /** @type {boolean} */
  #imperial = false;

  /** @type {boolean} False while the price still reflects the untouched minimum size. */
  #hasAdjusted = false;

  /** @type {boolean} Set when the drawer closes because the item was added, not abandoned. */
  #addedToCart = false;

  connectedCallback() {
    super.connectedCallback();

    this.#imperial = this.dataset.unitSystem === "imperial";

    this.#width = this.#parseNumber(this.dataset.width, 0);
    this.#length = this.#parseNumber(this.dataset.length, 0);
    this.#pricePerM2 = this.#parseNumber(this.dataset.pricePerM2, 0);
    this.#minorUnitFactor = this.#parseNumber(this.dataset.minorUnitFactor, 100);
    if (this.#minorUnitFactor <= 0) this.#minorUnitFactor = 100;
    const shape = this.dataset.shape;
    if (shape === "rectangle" || shape === "oval" || shape === "round") this.#shape = shape;
    if (this.#shape === "round") this.#length = this.#width;
    if (this.dataset.moneyFormat) this.#moneyFormat = this.dataset.moneyFormat;
    this.#standardSizes = this.#parseStandardSizes(this.dataset.standardSizes);
    this.#variants = this.#parseVariants(this.dataset.variants);
    if (this.dataset.customSizeLabel) this.#sizeLabel = this.dataset.customSizeLabel;

    this.addEventListener("dimension:change", this.#handleDimensionChange);
    this.addEventListener("change", this.#handleOptionChange);
    this.addEventListener("click", this.#handleClick);
    document.addEventListener(ThemeEvents.drawerClose, this.#handleDrawerClose);

    this.#selectedOptionsText = this.#buildSelectedOptionsText();
    if (this.#imperial) this.#applyImperialIncrements();
    this.#updateSummary();
    this.#updatePrice();
    this.#updateStandardMatch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("dimension:change", this.#handleDimensionChange);
    this.removeEventListener("change", this.#handleOptionChange);
    this.removeEventListener("click", this.#handleClick);
    document.removeEventListener(ThemeEvents.drawerClose, this.#handleDrawerClose);
  }

  #handleDrawerClose = (event) => {
    const { content } = event.detail ?? {};
    if (content instanceof Node && !content.contains(this)) return;

    if (this.#addedToCart) {
      this.#addedToCart = false;
      return;
    }

    this.reset();
  };

  #handleClick = (event) => {
    if (event.target instanceof Element && event.target.closest("[data-standard-size-link]")) {
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.customSizeExit, {
          detail: { productId: this.dataset.productId ?? "" }
        })
      );
      return;
    }

    toggleBackingInfoFromEvent(event, this);
  };

  #handleOptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.type !== "checkbox" || !event.target.dataset.optionName) return;

    this.#hasAdjusted = true;
    this.#selectedOptionsText = this.#buildSelectedOptionsText();
    this.#updateSummary();
    this.#updatePrice();
  };

  #handleDimensionChange = (event) => {
    const { dimension, value, userInitiated = true } = event.detail;

    if (dimension === "width") {
      this.#width = value;
      if (this.#shape === "round") this.#length = value;
    } else if (dimension === "length") {
      this.#length = value;
    } else {
      return;
    }

    if (userInitiated) this.#hasAdjusted = true;

    this.dataset.width = String(this.#width);
    this.dataset.length = String(this.#length);

    this.#updateSummary();
    this.#updatePrice();
    this.#updateStandardMatch();
  };

  reset() {
    const width = this.#parseNumber(this.dataset.defaultWidth, this.#width);
    const length = this.#parseNumber(this.dataset.defaultLength, this.#length);

    this.#width = width;
    this.#length = this.#shape === "round" ? width : length;
    this.dataset.width = String(this.#width);
    this.dataset.length = String(this.#length);

    for (const checkbox of this.querySelectorAll('input[type="checkbox"][data-option-name]')) {
      if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
    }

    for (const slider of this.querySelectorAll("custom-size-slider")) {
      if (typeof slider.setValueCm !== "function") continue;
      const value = slider.dataset.dimension === "length" ? this.#length : this.#width;
      slider.setValueCm(value, { silent: true });
    }

    this.#hasAdjusted = false;
    this.#selectedOptionsText = this.#buildSelectedOptionsText();
    this.#updateSummary();
    this.#updatePrice();
    this.#updateStandardMatch();
  }

  async addToCart() {
    if (this.#isAdding) return;

    const variant = this.#getSelectedVariant();
    if (!variant) return;

    this.#isAdding = true;
    this.#setAddingState(true);

    try {
      const body = JSON.stringify({
        id: variant.id,
        quantity: 1,
        properties: this.#buildLineItemProperties()
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig("json", { body }));

      if (!response.ok) return;

      document.dispatchEvent(
        new CartAddEvent({}, "custom-size-configurator", {
          source: "custom-size-configurator",
          variantId: String(variant.id),
          itemCount: 1
        })
      );

      this.#addedToCart = true;
      this.#closeDrawer();
    } finally {
      this.#isAdding = false;
      this.#setAddingState(false);
    }
  }

  sendInquiry() {
    const baseUrl = this.dataset.inquiryFormUrl;

    if (!baseUrl) {
      console.error("custom-size-configurator: data-inquiry-form-url is missing.");
      return;
    }

    const url = buildInquiryFormUrl({
      baseUrl,
      widthCm: this.#width,
      lengthCm: this.#length,
      articleNumber: this.#getSelectedVariant()?.sku || this.dataset.inquiryArticleNumber || "",
      productName: this.dataset.inquiryProductName || "",
      productColour: this.dataset.inquiryProductColour || "",
      productDescription: this.dataset.inquiryProductDescription || "",
      ...customerFromDataset(this.dataset),
      imperial: this.#imperial
    });

    window.location.assign(url);
  }

  #closeDrawer() {
    const drawer = this.closest("drawer-component") || document.querySelector("drawer-component:not([id])");
    if (drawer && typeof drawer.close === "function") {
      drawer.close();
    }
  }

  #getSelectedVariant() {
    if (this.#variants.length === 0) return null;

    const backingOn = this.#isOptionChecked(OPTION_BACKING);
    const underlayOn = this.#isOptionChecked(OPTION_UNDERLAY);

    return (
      this.#variants.find(
        (variant) => Boolean(variant.backing) === backingOn && Boolean(variant.underlay) === underlayOn
      ) || null
    );
  }

  #isOptionChecked(optionName) {
    return this.#getOptionCheckbox(optionName)?.checked ?? false;
  }

  #getOptionCheckbox(optionName) {
    const checkbox = this.querySelector(`input[type="checkbox"][data-option-name="${optionName}"]`);
    return checkbox instanceof HTMLInputElement ? checkbox : null;
  }

  #buildLineItemProperties() {
    /** @type {Record<string, string>} */
    const properties = {};

    if (this.#shape === "round") {
      properties[this.#sizeLabel] = `Ø ${this.#width} cm`;
      properties["_diameter_cm"] = String(this.#width);
    } else {
      properties[this.#sizeLabel] = `${this.#width}x${this.#length} cm`;
      properties["_w_cm"] = String(this.#width);
      properties["_l_cm"] = String(this.#length);
    }

    return properties;
  }

  #setAddingState(adding) {
    const button = this.querySelector("[data-add-to-cart]");
    if (button instanceof HTMLButtonElement) {
      button.disabled = adding;
      button.classList.toggle("opacity-60", adding);
    }
  }

  #parseVariants(raw) {
    if (!raw) return [];

    try {
      const list = JSON.parse(raw);
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  #buildSelectedOptionsText() {
    let text = "";

    for (const checkbox of this.querySelectorAll('input[type="checkbox"][data-option-name]')) {
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) continue;

      const label = checkbox.dataset.displayLabel?.trim() || checkbox.value?.trim();
      if (label) text += ` • ${label}`;
    }

    return text;
  }

  #updateSummary() {
    const text = `${this.#formatSize()}${this.#selectedOptionsText}`;

    for (const el of this.querySelectorAll("[data-size-display]")) {
      el.textContent = text;
    }
  }

  #formatSize() {
    if (this.#imperial) {
      if (this.#shape === "round") return `Ø ${formatFeetInches(cmToInch(this.#width))}`;
      return `${formatFeetInches(cmToInch(this.#width))} x ${formatFeetInches(cmToInch(this.#length))}`;
    }

    if (this.#shape === "round") return `Ø ${this.#width} cm`;
    return `${this.#width} x ${this.#length} cm`;
  }

  #applyImperialIncrements() {
    const template = this.dataset.incrementsTemplate;
    if (!template) return;

    for (const el of this.querySelectorAll("[data-increment]")) {
      const cm = Number(el.dataset.incrementCm);
      if (!Number.isFinite(cm) || cm <= 0) continue;
      const inches = Math.max(1, cmToInchCeil(cm));
      el.textContent = template.replace("__INCREMENT__", formatFeetInches(inches));
    }
  }

  #updatePrice() {
    const totalMinorUnits = this.#getTotalMinorUnits();
    if (totalMinorUnits <= 0) return;

    const text = this.#formatMoney(totalMinorUnits);

    for (const el of this.querySelectorAll("[data-total-price]")) {
      el.textContent = text;
    }

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.customSizePriceChange, {
        detail: { productId: this.dataset.productId ?? "" }
      })
    );
  }

  get currentPrice() {
    const totalMinorUnits = this.#getTotalMinorUnits();
    if (totalMinorUnits <= 0) return null;

    return { price: this.#formatMoney(totalMinorUnits), isMinimum: !this.#hasAdjusted };
  }

  #getPricePerM2Minor() {
    const variant = this.#getSelectedVariant();
    if (variant && Number.isFinite(variant.pricePerM2)) return variant.pricePerM2;
    return this.#pricePerM2;
  }

  #getTotalMinorUnits() {
    return calculateTotalMinorUnits({
      pricePerM2MinorUnits: this.#getPricePerM2Minor(),
      minorUnitFactor: this.#minorUnitFactor,
      shape: this.#shape,
      widthCm: this.#width,
      lengthCm: this.#length
    });
  }

  #formatMoney(minorUnits) {
    return formatMoney(minorUnits, this.#minorUnitFactor, this.#moneyFormat);
  }

  #parseStandardSizes(raw) {
    if (!raw) return new Set();

    try {
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return new Set();
      return new Set(list.map((value) => this.#toComparisonUnit(this.#normalizeSize(value))).filter(Boolean));
    } catch {
      return new Set();
    }
  }

  #normalizeSize(value) {
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[×x]/g, "x")
      .replace(/[ø⌀]/g, "")
      .replace(/cm$/g, "");
  }

  #toComparisonUnit(normalized) {
    if (!this.#imperial) return normalized;
    return normalized.replace(/\d+/g, (cm) => String(cmToInch(Number(cm))));
  }

  #matchesStandardSize() {
    if (this.#standardSizes.size === 0) return false;
    const key = this.#shape === "round" ? `${this.#width}` : `${this.#width}x${this.#length}`;
    return this.#standardSizes.has(this.#toComparisonUnit(this.#normalizeSize(key)));
  }

  #updateStandardMatch() {
    const isMatch = this.#matchesStandardSize();
    this.dataset.standardMatch = String(isMatch);

    const note = this.querySelector("[data-standard-size-note]");
    if (note) note.classList.toggle("hidden", !isMatch);

    const cta = this.querySelector("[data-add-to-cart], [data-send-inquiry]");
    if (cta instanceof HTMLButtonElement) cta.disabled = isMatch;
  }

  #parseNumber(raw, fallback) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
}

if (!customElements.get("custom-size-configurator")) {
  customElements.define("custom-size-configurator", CustomSizeConfigurator);
}
